import { EmpresasService } from './../../../services/empresa/empresas.service';
import { ActivatedRoute, Route, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { CadastrarEmpresaDadosGerais } from './cadastrar-empresa-dados-gerais/cadastrar-empresa-dados-gerais';
import { CadastrarEmpresaEstabelecimentos } from './empresa-listar-estabelecimentos/empresa-listar-estabelecimentos';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import axiosClient from '../../../../shared/axios/axios-client';
import { CadastrarEndereco } from './cadastrar-endereco/cadastrar-endereco';
import { EmpresaRequest, EmpresaResponse } from '../../../models/Empresa';
import { EstadosCidadesService } from '../../../services/estados-cidades/estados-cidades.service';
import { AuthService } from '../../../services/auth/auth.service';
import { formatarCEP, formatarCNPJ, formatarTelefone } from '../../../shared/formatter';
import { clearPaginas, pushPaginaAtiva } from '../../../shared/activePage';

@Component({
  selector: 'app-cadastrar-empresa',
  standalone: true,
  imports: [
    MatButtonModule,
    MatInputModule,
    MatSnackBarModule,
    CommonModule,
    ReactiveFormsModule,
    CadastrarEmpresaDadosGerais,
    CadastrarEmpresaEstabelecimentos,
    CadastrarEndereco,
  ],
  templateUrl: './cadastrar-empresa.html',
  styleUrls: ['./cadastrar-empresa.scss'],
})
export class CadastrarEmpresa implements OnInit {
  // Constantes para controle de navegação
  readonly CADASTRAR_DADOS_GERAIS = 'CADASTRAR_DADOS_GERAIS';
  readonly LISTAR_ESTABELECIMENTOS = 'LISTAR_ESTABELECIMENTOS';
  readonly CADASTRAR_ENDERECOS = 'CADASTRAR_ENDERECOS';

  activeView: string = this.CADASTRAR_DADOS_GERAIS;
  empresaForm: FormGroup;
  empresaSalva: any = null;
  dadosCompartilhados: any = {};

  idEmpresa = 0;
  estabelecimentoParaEditar: any = signal(null);

  modoEdicaoAtivo = signal(false);
  modoEdicaoEstabelecimento = signal(false);

  podeCadastrarEndereco = signal(true);

  cidadeOptionsFiltradas: Array<{
    id_cidade: number;
    nome: string;
    id_uf: number;
  }> = [];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private snackBarService: SnackbarService,
    private empresaService: EmpresasService,
    private estadosCidadesService: EstadosCidadesService,
    private authService: AuthService
  ) {
    this.empresaForm = this.fb.group({});
    this.initializeForm();
    this.setupSharedData();
  }

  isModoEdicao() {
    return !!this.route.snapshot.paramMap.get('id');
  }

  async ngOnInit(): Promise<void> {
    clearPaginas();
    pushPaginaAtiva({
        nome: 'Home',
        rota: '/home'
    });
    pushPaginaAtiva({
        nome: 'Empresas cadastradas',
        rota: '/empresas'
    });
    pushPaginaAtiva({
        nome: 'Cadastrar empresa',
        rota: ''
    });
    if (!!this.route.snapshot.paramMap.get('id')) {
      this.idEmpresa = Number(this.route.snapshot.paramMap.get('id'));
      this.modoEdicaoAtivo.set(true);
    }

    this.podeCadastrarEndereco.set(this.authService.isMaster());

    if (this.modoEdicaoAtivo()) {
      try {
        const empresaResponse: EmpresaResponse =
          await this.empresaService.buscarEmpresaPorId(this.idEmpresa);
        this.preencherFormulario(empresaResponse);
        this.empresaSalva = empresaResponse;
        this.updateSharedData();
      } catch (err: any) {
        this.snackBarService.error(
          err.response?.data?.error || 'Erro ao carregar empresa'
        );
      }
    }
  }

  private initializeForm(): void {
    this.empresaForm = this.fb.group({
      cnpj: ['', [Validators.required]],
      razaoSocial: ['', [Validators.required]],
      nomeFantasia: ['', [Validators.required]],
      isMatriz: [true],
      idGrupo: [5],
      cep: ['', [Validators.required]],
      logradouro: ['', [Validators.required]],
      numero: [''],
      complemento: [''],
      ufResidencia: ['', [Validators.required]],
      cidadeResidencia: ['', [Validators.required]],
      nomeCompleto: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      cargo: ['', [Validators.required]],
      telefoneCelular: ['', [Validators.required]],
      contatosAdicionais: this.fb.array([]),
    });
  }

  onAcessarEstabelecimento(estabelecimento: any): void {
    this.dadosCompartilhados.estabelecimentoParaEditar = {
      ...estabelecimento,
      idEmpresa: this.idEmpresa,
    };
    this.dadosCompartilhados.modoEdicaoEstabelecimento = true;
    this.changeActiveScreen(this.CADASTRAR_ENDERECOS);
  }

  private setupSharedData(): void {
    this.dadosCompartilhados = {
      // Dados
      empresaForm: this.empresaForm,
      empresaData: this.getEmpresaData(),
      empresaSalva: this.empresaSalva,
      axiosClient: axiosClient,

      modoEdicaoAtivo: this.isModoEdicao(),
      estabelecimentoParaEditar: this.estabelecimentoParaEditar,

      onAcessarEstabelecimento: (estabelecimento: any) =>
        this.onAcessarEstabelecimento(estabelecimento),
      onCancelarEdicaoEstabelecimento: () =>
        this.cancelarEdicaoEstabelecimento(),

      // Método para atualizar estabelecimento
      onAtualizarEstabelecimento: (estabelecimentoData: any) =>
        this.atualizarEstabelecimento(estabelecimentoData),

      onCadastrarEstabelecimento: (estabelecimentoData: any) =>
        this.onCadastrarEstabelecimento(estabelecimentoData),

      // Constantes
      CADASTRAR_DADOS_GERAIS: this.CADASTRAR_DADOS_GERAIS,
      LISTAR_ESTABELECIMENTOS: this.LISTAR_ESTABELECIMENTOS,
      CADASTRAR_ENDERECOS: this.CADASTRAR_ENDERECOS,

      // Métodos
      changeActiveScreen: (view: string) => this.changeActiveScreen(view),
      getEmpresaData: () => this.getEmpresaData(),
      getEmpresaRequestData: () => this.getEmpresaRequestData(),
      updateForm: (field: string, value: any) => this.updateForm(field, value),
      onChangeUF: (idUF: number) => this.onChangeUF(idUF),
      onSalvar: () => this.saveDadosGerais(),
      onAdicionarEstabelecimento: () => this.adicionarEndereco(),

      // Opções para selects (removemos cidadeOptions daqui pois será filtrada)
      ufOptions: this.estadosCidadesService.getUfOptions(),

      // Cidades já filtradas e cacheadas
      cidadeOptionsFiltradas: this.cidadeOptionsFiltradas,

      // Service para uso nos componentes filhos (se necessário)
      estadosCidadesService: this.estadosCidadesService,
    };
  }

  cancelarEdicaoEstabelecimento(): void {
    this.estabelecimentoParaEditar.set(null);
    this.modoEdicaoEstabelecimento.set(false);
    this.changeActiveScreen(this.LISTAR_ESTABELECIMENTOS);
  }

  retornaParaListagem() {
    this.router.navigate(['empresas']);
  }

  // Adicione este método para atualizar estabelecimento
  async atualizarEstabelecimento(estabelecimentoData: any): Promise<void> {
    try {
      const res = await axiosClient.put(
        `/estabelecimento`,
        estabelecimentoData
      );

      this.snackBarService.success('Endereço atualizado com sucesso!');
      this.cancelarEdicaoEstabelecimento();

      this.retornaParaListagem();
    } catch (error: any) {
      console.error('Erro ao atualizar estabelecimento:', error);
      this.snackBarService.error(
        error.response?.data?.message || 'Erro ao atualizar endereço'
      );
    }
  }

  async onCadastrarEstabelecimento(estabelecimentoData: any): Promise<void> {
    try {
      const res = await axiosClient.post(
        `/estabelecimento`,
        estabelecimentoData
      );

      this.snackBarService.success('Endereço cadastrado com sucesso!');
      this.cancelarEdicaoEstabelecimento();

      this.retornaParaListagem();
    } catch (error: any) {
      console.error('Erro ao cadastrar estabelecimento:', error);
      this.snackBarService.error(
        error.response?.data?.message || 'Erro ao cadastrar endereço'
      );
    }
  }

  // Adicione este método para recarregar estabelecimentos
  onRecarregarEstabelecimentos(): void {
    // Este método será implementado no componente de listagem
  }

  getEmpresaData(): any {
    const formValue = this.empresaForm.value;

    return {
      id: this.empresaSalva?.id || null,
      cnpj: formValue.cnpj,
      nomeFantasia: formValue.nomeFantasia,
      razaoSocial: formValue.razaoSocial,
      enderecos: [
        {
          cep: formValue.cep,
          logradouro: formValue.logradouro,
          numero: formValue.numero,
          complemento: formValue.complemento,
          estado: this.getEstadoSigla(formValue.ufResidencia),
          cidade: this.getCidadeNome(formValue.cidadeResidencia),
        },
      ],
      contatos: [
        {
          nomeContato: formValue.nomeCompleto,
          emailContato: formValue.email,
          cargoContato: formValue.cargo,
          telefoneContato: formValue.telefoneCelular,
        },
      ],
    };
  }

  // Métodos de navegação
  changeActiveScreen(newView: string): void {
    this.activeView = newView;
    this.updateSharedData();
  }

  adicionarEndereco(): void {
    this.changeActiveScreen(this.CADASTRAR_ENDERECOS);
  }

  // Métodos de manipulação de dados
  updateForm(field: string, value: any): void {
    this.empresaForm.get(field)?.setValue(value);
    this.updateSharedData();
  }

  onChangeUF(idUF: number): void {
    this.empresaForm.get('ufResidencia')?.setValue(idUF);
    this.empresaForm.get('cidadeResidencia')?.setValue('');

    // Atualizar cidades filtradas uma única vez
    this.atualizarCidadesFiltradas(idUF);

    this.updateSharedData();
  }

  private atualizarCidadesFiltradas(codigoUf: number): void {
    if (!codigoUf) {
      this.cidadeOptionsFiltradas = [];
      return;
    }

    this.cidadeOptionsFiltradas = this.estadosCidadesService
      .getMunicipiosPorEstadoCodigo(codigoUf)
      .map((municipio) => ({
        id_cidade: municipio.codigo_ibge,
        nome: municipio.nome,
        id_uf: municipio.codigo_uf,
      }));
  }

  private preencherFormulario(empresa: EmpresaResponse): void {
    // Preencher dados básicos
    this.empresaForm.patchValue({
      cnpj: empresa.cnpj,
      razaoSocial: empresa.razaoSocial,
      nomeFantasia: empresa.nomeFantasia,
    });

    // Preencher endereço principal
    if (empresa.enderecos.length > 0) {
      const endereco = empresa.enderecos[0];

      const { estadoCodigo, municipioCodigo } =
        this.estadosCidadesService.resolverEnderecoBackend({
          estado: endereco.estado,
          cidade: endereco.cidade,
        });

      this.empresaForm.patchValue({
        cep: endereco.cep,
        logradouro: endereco.logradouro,
        numero: endereco.numero,
        complemento: endereco.complemento,
        ufResidencia: estadoCodigo,
      });

      // Atualizar cidades filtradas ANTES de definir a cidade
      if (estadoCodigo) {
        this.atualizarCidadesFiltradas(estadoCodigo);

        // Agora sim definir a cidade
        if (municipioCodigo) {
          this.empresaForm.patchValue({
            cidadeResidencia: municipioCodigo,
          });
        }
      }
    }

    // Preencher contato principal
    if (empresa.contatos.length > 0) {
      const contato = empresa.contatos[0];
      this.empresaForm.patchValue({
        nomeCompleto: contato.nomeContato,
        email: contato.emailContato,
        cargo: contato.cargoContato,
        telefoneCelular: contato.telefoneContato,
      });
    }
  }

  private updateSharedData(): void {
    this.dadosCompartilhados.empresaData = this.getEmpresaData();
    this.dadosCompartilhados.empresaSalva = this.empresaSalva;
  }

  getEmpresaRequestData(): EmpresaRequest {
    const formValue = this.empresaForm.value;

    return {
      id: this.empresaSalva?.id || null,
      cnpj: formatarCNPJ(formValue.cnpj),
      razaoSocial: formValue.razaoSocial,
      nomeFantasia: formValue.nomeFantasia,
      enderecoPrincipal: {
        id: this.empresaSalva?.enderecoPrincipal?.id || null,
        cep: formatarCEP(formValue.cep),
        logradouro: formValue.logradouro,
        numero: formValue.numero,
        complemento: formValue.complemento,
        estado: this.getEstadoSigla(formValue.ufResidencia),
        cidade: this.getCidadeNome(formValue.cidadeResidencia),
      },
      contatoPrincipal: {
        id: this.empresaSalva?.contatoPrincipal?.id || null,
        nome: formValue.nomeCompleto,
        email: formValue.email,
        cargo: formValue.cargo,
        telefone: formatarTelefone(formValue.telefoneCelular),
      },
    };
  }

  private formatarResumoEndereco(formValue: any): string {
    return `${formValue.logradouro}, ${formValue.numero || 'S/N'}${
      formValue.complemento ? ' - ' + formValue.complemento : ''
    },
            ${this.getCidadeNome(
              formValue.cidadeResidencia
            )}/${this.getEstadoSigla(formValue.ufResidencia)},
            CEP: ${formValue.cep}`;
  }

  // Métodos de serviço
  async saveDadosGerais(): Promise<void> {
    if (this.empresaForm.invalid) {
      this.snackBarService.error(
        'Por favor, preencha todos os campos obrigatórios.'
      );
      return;
    }

    try {
      let res;
      let requestData = this.getEmpresaRequestData();

      if (this.modoEdicaoAtivo() && this.empresaSalva?.id) {
        requestData.id = this.empresaSalva.id;
        requestData.enderecoPrincipal.id = this.empresaSalva.enderecos[0].id;
        requestData.contatoPrincipal.id = this.empresaSalva.contatos[0].id;
        res = await axiosClient.put(`/empresa`, requestData);
      } else {
        res = await axiosClient.post('/empresa', requestData);
      }

      this.empresaSalva = res.data;
      this.updateSharedData();
      this.snackBarService.success('Dados salvos com sucesso!');
      this.retornaParaListagem();
    } catch (err: any) {
      console.error(err);
      this.snackBarService.error(
        err.response?.data?.error || 'Erro ao salvar dados'
      );
    }
  }

  // Métodos auxiliares usando o service
  private getEstadoSigla(codigoUf: number): string {
    const estado = this.estadosCidadesService.getEstadoPorCodigo(codigoUf);
    return estado?.uf || '';
  }

  private getCidadeNome(codigoIbge: number): string {
    const municipio =
      this.estadosCidadesService.getMunicipioPorCodigoIBGE(codigoIbge);
    return municipio?.nome || '';
  }

  // Métodos de verificação de tela ativa
  isCadastrarDadosGeraisActive(): boolean {
    return this.activeView === this.CADASTRAR_DADOS_GERAIS;
  }

  isListarEnderecosActive(): boolean {
    return this.activeView === this.LISTAR_ESTABELECIMENTOS;
  }

  isCadastrarEstabelecimentosActive(): boolean {
    return this.activeView === this.CADASTRAR_ENDERECOS;
  }
}
